import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Cable,
  Eye,
  EyeOff,
  Loader2,
  MousePointerSquareDashed,
  Pencil,
  Plus,
  Redo2,
  Search,
  Undo2,
  X,
} from "lucide-react";
import clsx from "clsx";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type { CablePlanTakeoff } from "../utils/fetchCables";
import type { EditableCableTakeoff } from "./useEditableCableTakeoff";
import type { CableDestinationInfo } from "./useCableDestinationInfo";
import { CircuitCardList } from "./cards";
import type { FeedPointOption } from "./cards";
import { RemovedDevices } from "./RemovedDevices";
import { UnroutedDevicesNote } from "./UnroutedDevicesNote";
import { TrayList } from "./TrayList";
import { PlanRecalculate } from "./PlanRecalculate";
import { PanelSectionHeader } from "./PanelSection";
import { PlanSetupRow } from "./PlanSetupRow";
import { WallList } from "./WallList";
import { RoomList, type RoomListProps } from "./RoomList";
import { countWallReview } from "./countWallReview";
import type { DetectedLayoutSummary } from "./planWalls";
import { PlanSetupProgress } from "./PlanSetupProgress";
import { buildSetupProgress } from "./buildSetupProgress";
import { useFirstCalculationPending } from "./useFirstCalculationPending";
import type { PlanSetupCount } from "./PlanSetupRow";
import { buildSourceRoster } from "./SourceList/buildSourceRoster";
import { buildTrayRoster } from "./TrayList/buildTrayRoster";
import { SourceList } from "./SourceList";
import { qualifyRunName } from "./qualifyRunName";
import { summariseCableTypes } from "./summariseCableTypes";
import { CableTypeSummary } from "./CableTypeSummary";
import { cableTypeSplitsOf } from "./circuitLegs";
import { feederCircuitRows } from "./feederCircuits";
import { formatMetres } from "./formatMetres";
import { editStepPhraseKey, stepRunId } from "./editStepSummary";
import type { EditStepSummary } from "./editStepSummary";
import type { CircuitKeyMap } from "./circuitKeys";
import type { CircuitBreakerMap } from "./circuitBreakers";
import type { KarlDiagramControl } from "./CablesChatPanel/types";
import { filterCableRows } from "./cableSearch";
import { AddRunForm } from "./AddRunForm";
import type { AddRunBoardOption, NewRun } from "./AddRunForm";
import { addedCircuitRows, buildAddedCircuitNames, mintCircuitId } from "./addedCircuits";
import { appCircuitRows } from "./appCircuits";
import { AssignModePanel } from "./AssignModePanel";
import { buildAssignTargets, groupUnassigned, nextCircuitKey, type AssignableDevice } from "./assignMode";
import { DEFAULT_SPLIT_SIZE, FALLBACK_SPLIT_SIZE, splitIntoRows } from "./splitCircuit";
import { splitByRooms, type AutoSplitPlan } from "./splitByRooms";
import { findRoomSwitches } from "./findRoomSwitches";
import { planRoomCircuits, type RoomCircuitsPlan } from "./planRoomCircuits";
import { roomCircuitName } from "./roomCircuitName";
import { switchingRoleOf } from "./switchingRoleOf";
import type {
  FloorPlanLayoutRoom,
  FloorPlanOpening,
} from "../utils/types";
import type { AssignModeControls } from "../hooks/useCableTakeoff/types";
import type { CircuitName } from "./circuitNames";
import { sumTypesByCategory } from "./boardFeeds";
import { CATEGORY_META, type DeviceType } from "./deviceCategories";
import type { UnfilledWay } from "./unfilledWays";
import type { PageScaleSource } from "../utils/types";
import type { CableTraySegment } from "../utils/fetchCableTakeoffPaths";
import type { DrawnRouteType } from "./TrayList";
import type { RouteStatusKind } from "./karlRoutes";
import type { UnroutedSummaryView } from "./unroutedDevices";
import type { WallListProps } from "./WallList";
import type { ZoneDraft } from "./edits/useCableDirectEdit";

// A stable empty map, so a panel rendered without an edit store does not mint a
// fresh object each render and re-derive every row behind it.
const NO_ADDED_CIRCUITS: NonNullable<EditableCableTakeoff["addedCircuits"]> = {};
const IDLE_ROUTING: EditableCableTakeoff["routing"] = { phase: "idle" };

/** The four sections above the runs. One is open at a time. */
type AsideSection = "sources" | "removed" | "trays" | "walls" | "rooms" | "unrouted";

// The word is the accessible NAME and the step is the description, exactly as
// when the word was printed: the icon is glossed, never unnamed.
const HISTORY_CLASS =
  "grid h-9 w-9 flex-none place-items-center rounded-md border border-slate-300 bg-white text-blue-600 hover:border-blue-500 hover:bg-blue-50 disabled:border-slate-200 disabled:bg-white disabled:text-slate-300";

export function CableTakeoffTabV3({
  takeoff,
  selectedCircuit,
  underReviewCircuit,
  onSpotlightDevices,
  onFeedArea,
  openBoard,
  onOpenBoard,
  onCableTypeCircuits,
  zoneDraft,
  onFinishZone,
  isPickingRun,
  onPickRun,
  onAskKarlAbout,
  onSelectCircuit,
  onHoverCircuit,
  edit,
  grabbed,
  onCancelGrab,
  onResetAll,
  onUnroutedListOpenChange,
  assignMode,
  circuitNames,
  onAddSource,
  trays,
  drawnRouteTypes,
  onSetCarriesCable,
  areTraysShown = true,
  onToggleTrays,
  onHighlightTrayType,
  destinationInfo,
  destinationInfoById,
  onHighlightDestination,
  onShowHeightMarks,
  pageScaleValue,
  pageScaleSource,
  onChangePageScale,
  isChangePageScaleDisabled,
  routeStatus,
  feedPointOptions,
  cableTypeOptions,
  circuitKeys,
  circuitBreakers,
  diagram,
  unrouted,
  unfilledWays,
  areAllCablesShown = true,
  onToggleAllCables,
  walls,
  rooms,
  roomLayout,
  onRoomTint,
  detailHost,
  detectedWallsSummary,
}: {
  takeoff: CablePlanTakeoff | null;
  selectedCircuit?: string | null;
  /** The run Karl is asking about right now. */
  underReviewCircuit?: string | null;
  /** Ring and pulse devices on the plan; null hands the rings back. */
  onSpotlightDevices?: (positions: { x: number; y: number }[] | null) => void;
  /** True while Karl is waiting to be told which run some devices belong on. */
  isPickingRun?: boolean;
  onPickRun?: (circuitId: string) => void;
  /** Puts a run to Karl in the thread. */
  onAskKarlAbout?: (circuitId: string) => void;
  onSelectCircuit?: (circuitId: string | null) => void;
  onHoverCircuit?: (circuitId: string | null) => void;
  edit?: EditableCableTakeoff;
  grabbed?: {
    kind: "source" | "new_source" | "node" | "main_board" | "feeder_for" | "upstream_for";
    isFeedIn?: boolean;
    label: string;
  } | null;
  onCancelGrab?: () => void;
  /** Resets the edits and Karl's answers together; Karl's old answers would otherwise close the steps again. */
  onResetAll?: () => void;
  /** Tells the plan whether the devices-on-no-run list is open, so it draws their markers. */
  onUnroutedListOpenChange?: (isOpen: boolean) => void;
  /** The assignment mode; absent = not offered. */
  assignMode?: AssignModeControls;
  /** What every circuit is called; replaces the agent's internal ids on the rows. */
  circuitNames?: Record<string, CircuitName>;
  /** Arms plan placement for a new board with this name. */
  onAddSource?: (name: string) => void;
  /** Arms the polygon tool that draws a board's zone. */
  onFeedArea?: (sourceLabel: string) => void;
  /** The board opened in the runs list; the plan narrows to it. */
  openBoard?: string | null;
  /** The circuits of the cable type picked in the summary, so the plan shows only them. */
  onCableTypeCircuits?: (circuitIds: string[] | null) => void;
  onOpenBoard?: (board: string | null) => void;
  /** The zone being drawn, so the panel can say how to close it. */
  zoneDraft?: ZoneDraft | null;
  onFinishZone?: () => void;
  /** The infrastructure the cables ride in, drawn on the plan. */
  trays?: CableTraySegment[];
  /**
   * Every drawn route type and whether cable rides it. The vocabulary decides by
   * label and was silent when it was wrong, so the list shows its verdict and
   * he can overrule it.
   */
  drawnRouteTypes?: DrawnRouteType[];
  onSetCarriesCable?: (
    routeTypeId: number,
    carriesCable: boolean | null,
  ) => void;
  areTraysShown?: boolean;
  onToggleTrays?: (shown: boolean) => void;
  onHighlightTrayType?: (type: string | null) => void;
  /** Per-circuit resolved destinations (name + symbol icon) for the cards. */
  destinationInfo?: Record<string, CableDestinationInfo[]>;
  /** Destinations keyed by id, including the ones an edit removed. */
  destinationInfoById?: Record<string, CableDestinationInfo>;
  onHighlightDestination?: (pos: { x: number; y: number } | null) => void;
  /** Marks where the pinned device's cable rises and drops; empty clears them. */
  onShowHeightMarks?: (marks: { x: number; y: number; kind: "rise" | "drop" }[]) => void;
  /** Shared page scale (metersPerPdfPoint) — same value Kabelgruppen uses. */
  pageScaleValue?: number;
  pageScaleSource?: PageScaleSource | null;
  onChangePageScale?: () => void;
  isChangePageScaleDisabled?: boolean;
  /** Routes the estimator has settled with Karl, keyed by circuit id. */
  routeStatus?: Record<string, RouteStatusKind>;
  /** Boards on this plan, for changing which one a run starts from. */
  feedPointOptions?: FeedPointOption[];
  /** Cable types already used in this project, as suggestions. */
  cableTypeOptions?: string[];
  /** The printed identifier per circuit — the thing he scans a list of 83 for. */
  circuitKeys?: CircuitKeyMap;
  /** The schematic's breaker designator per circuit, where the schedule named
   * one and it is not the id the plan prints. */
  circuitBreakers?: CircuitBreakerMap;
  /** Finds a run on the Verteilerplan; absent = no schematic loaded. */
  diagram?: KarlDiagramControl;
  /** Devices the run put on no circuit at all, counted and reasoned. */
  unrouted?: UnroutedSummaryView;
  /**
   * The schedule's own rows that became no run. They are what makes creating
   * A5 one click rather than four fields: the id, the board, the cable type and
   * the designator are already in the schematic, and retyping them is how a
   * created run comes to disagree with the document it came from.
   */
  unfilledWays?: UnfilledWay[];
  /**
   * Whether the plan is drawing every cable at once. Off is the default, and
   * the reason is the frame rate rather than the mess: *"Showing all routes at
   * once is somehow lagging always."*
   */
  areAllCablesShown?: boolean;
  onToggleAllCables?: (shown: boolean) => void;
  /**
   * Walls he has drawn, and the tool that draws them. A wall carries no cable
   * and no metres — it is why a run goes the long way round, and the router is
   * the only thing that reads it.
   */
  walls?: WallListProps;
  /** Where an opened run shows its details, beside the list; null keeps them inline under the row. */
  detailHost?: HTMLElement | null;
  /** Which room takes which preview colour while "Aus Räumen bilden" is open; null clears the tint. */
  onRoomTint?: (tint: Record<number, number> | null) => void;
  /** The page's rooms on the plan, and whether they are drawn; absent with no layout. */
  rooms?: Omit<RoomListProps, "isOpen" | "onOpenChange">;
  /** The page's rooms and doors from the plan's layout; "Automatisch aufteilen" splits by them. */
  roomLayout?: { rooms: FloorPlanLayoutRoom[]; doors: FloorPlanOpening[] };
  /** What the layout detector suggested for this page; absent when it has not run. */
  detectedWallsSummary?: DetectedLayoutSummary;
}) {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState("");
  // One open at a time. Every section used to keep its own answer, so opening
  // the second of four left all of them open and the runs list with whatever
  // was left of the column.
  const [openSection, setOpenSection] = useState<AsideSection | null>(null);
  // The four of them live behind ONE row at the foot of the panel: the runs are
  // what he is here for, and they now lead.
  const [isSetupOpenByHim, setIsSetupOpenByHim] = useState(false);
  const [isAddingRun, setIsAddingRun] = useState(false);
  const metres = (value: number): string =>
    formatMetres({ value, language: i18n.language });

  const recalculate = async (onlyCircuitIds?: string[]) => {
    if (!edit?.rerouteAllCircuits) return;
    const wasStale = edit.arePlanTraysStale;
    const changes = await edit.rerouteAllCircuits(onlyCircuitIds);
    // An empty answer means the router returned nothing, not a recalculation.
    if (changes.length === 0) return;
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_PLAN_RECALCULATED, {
      wasStale,
      runsRecalculated: changes.length,
      metresDelta:
        Math.round(
          changes.reduce(
            (deltaSum, change) => deltaSum + change.afterM - change.beforeM,
            0,
          ) * 10,
        ) / 10,
    });
  };
  // Nothing is calculated until Karl's three steps are done, so no run, type or total is shown before.
  const isSettingUp = Boolean(edit?.derivedGeometry) && !edit?.isSetupDone;
  const isFirstCalculationPending = useFirstCalculationPending({
    isSetupDone: Boolean(edit?.derivedGeometry && edit.isSetupDone),
    routing: edit?.routing ?? IDLE_ROUTING,
    openRequestCount: edit?.routeRequests.length ?? 0,
  });
  const isResultHidden = isSettingUp || isFirstCalculationPending;
  // Read before the early return below, so the filter memo is never conditional,
  // and memoised because `?? []` mints a new array every render — which would
  // re-filter 83 rows on each one.
  const publishedItems = useMemo(() => takeoff?.boq_items ?? [], [takeoff]);
  const addedCircuits = edit?.addedCircuits ?? NO_ADDED_CIRCUITS;
  // A run he created is a row like any other from here down: it is ordered,
  // searched, picked as a move target and priced by the same code, because
  // geometry alone gives a run on the plan with no card in the list.
  const addedRows = useMemo(
    () =>
      addedCircuitRows({
        circuits: addedCircuits,
        lengthsByCircuit: edit?.derivedLengthsByCircuit,
      }),
    [addedCircuits, edit?.derivedLengthsByCircuit],
  );
  // Circuits the app made to give a board its categories are rows too, named after the category.
  const appCircuits = edit?.appCircuits;
  const appRows = useMemo(
    () =>
      appCircuitRows({
        circuits: appCircuits ?? {},
        lengthsByCircuit: edit?.derivedLengthsByCircuit,
        categoryName: (category) => t(CATEGORY_META[category].i18nKey),
      }),
    [appCircuits, edit?.derivedLengthsByCircuit, t],
  );
  // The cables that feed a board lead the list, in a group of their own.
  const feederCircuits = edit?.feederCircuits;
  const feederRows = useMemo(
    () =>
      feederCircuitRows({
        feeders: feederCircuits ?? {},
        lengthsByCircuit: edit?.derivedLengthsByCircuit ?? {},
        cableTypeByCircuit: edit?.cableTypeByCircuit ?? {},
        boardName: (label) =>
          feedPointOptions?.find((option) => option.label === label)?.name ?? label,
        title: (board, isTypeConfirmed) =>
          t(
            isTypeConfirmed
              ? "smartSurveyor.cablesBeta.feederRowTitle"
              : "smartSurveyor.cablesBeta.feederRowTitleUnconfirmed",
            { board },
          ),
      }),
    [feederCircuits, edit?.derivedLengthsByCircuit, edit?.cableTypeByCircuit, feedPointOptions, t],
  );
  const deviceCountsByBoard = edit?.deviceCountsByBoard;
  const typesByCategory = useMemo(
    () => sumTypesByCategory(deviceCountsByBoard ?? {}),
    [deviceCountsByBoard],
  );
  const feedingBoardByCircuit = edit?.feedingBoardByCircuit;
  const items = useMemo(() => {
    const extra = [...feederRows, ...addedRows, ...appRows];
    const all = extra.length ? [...publishedItems, ...extra] : publishedItems;
    if (!feedingBoardByCircuit) return all;
    const boardName = (label: string) =>
      feedPointOptions?.find((option) => option.label === label)?.name ?? label;
    // Named after the board it feeds from now, so A66 on UV1 and A66 on UV2 never read alike.
    return all.map((item) => {
      const named = item.circuit ? circuitNames?.[item.circuit] : undefined;
      // An agent group's id ("V1·grp·…") is not a name: everything but a printed number gets the app's name.
      if (named && named.from !== "plan") return { ...item, text: named.name };
      if (!item.circuit || !item.text) return item;
      const text = qualifyRunName({
        text: item.text,
        runBoard: item.board,
        feedingBoard: feedingBoardByCircuit[item.circuit],
        boardName,
      });
      return text === item.text ? item : { ...item, text };
    });
  }, [publishedItems, feederRows, addedRows, appRows, feedingBoardByCircuit, feedPointOptions, circuitNames]);
  // The id it leads with and the designator under it, by the same rule the
  // estimation's runs follow — the row and every search over it name a created
  // run exactly once.
  const addedNames = useMemo(
    () => buildAddedCircuitNames({ circuits: addedCircuits }),
    [addedCircuits],
  );
  const keyOverrides = edit?.circuitKeyOverrides;
  const allCircuitKeys = useMemo(
    () =>
      Object.keys(addedNames.keys).length || (keyOverrides && Object.keys(keyOverrides).length)
        ? { ...circuitKeys, ...addedNames.keys, ...keyOverrides }
        : circuitKeys,
    [circuitKeys, addedNames.keys, keyOverrides],
  );
  const allCircuitBreakers = useMemo(
    () =>
      Object.keys(addedNames.breakers).length
        ? { ...circuitBreakers, ...addedNames.breakers }
        : circuitBreakers,
    [circuitBreakers, addedNames.breakers],
  );
  // Filtered here rather than inside the list: the grouping, the section headers
  // and the empty state all read off one array, so there is no second idea of
  // which runs are on screen.
  const cableTypeSummary = useMemo(
    () =>
      summariseCableTypes({
        items,
        cableTypeByCircuit: edit?.cableTypeByCircuit,
        lengthsByCircuit: edit?.derivedLengthsByCircuit,
        feedingBoardByCircuit,
        extraTypes: edit?.addedCableTypes,
        splitByCircuit: edit
          ? Object.fromEntries(
              Object.entries(
                cableTypeSplitsOf({
                  legsByCircuit: edit.legsByCircuit,
                  cableTypeByLegByCircuit: edit.cableTypeByLegByCircuit,
                  circuitTypeOf: (circuitId) =>
                    edit.cableTypeByCircuit[circuitId] ??
                    items.find((item) => item.circuit === circuitId)?.cable_type ??
                    "",
                }),
              ).map(([circuitId, shares]) => [
                circuitId,
                shares.map((share) => ({ cableType: share.cableType, metres: share.totalM })),
              ]),
            )
          : undefined,
      }),
    [items, edit, feedingBoardByCircuit],
  );
  // Every type the plan knows: the run's, the ones on a run now, and the ones he made.
  const allCableTypes = useMemo(
    () =>
      [
        ...new Set([
          ...(cableTypeOptions ?? []),
          ...Object.values(edit?.cableTypeByCircuit ?? {}),
          ...(edit?.addedCableTypes ?? []),
        ]),
      ]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [cableTypeOptions, edit?.cableTypeByCircuit, edit?.addedCableTypes],
  );
  const [cableTypeKey, setCableTypeKey] = useState<string | null>(null);
  const selectedCableType =
    cableTypeKey === null
      ? null
      : (cableTypeSummary.find((entry) => entry.key === cableTypeKey) ?? null);
  const typeCircuitIds = selectedCableType?.circuitIds ?? null;
  const typeItems = useMemo(() => {
    if (!typeCircuitIds) return items;
    const shown = new Set(typeCircuitIds);
    return items.filter((item) => item.circuit && shown.has(item.circuit));
  }, [items, typeCircuitIds]);
  const typeCircuitStamp = typeCircuitIds?.join("|") ?? null;
  useEffect(() => {
    onCableTypeCircuits?.(
      typeCircuitStamp === null ? null : typeCircuitStamp.split("|"),
    );
  }, [onCableTypeCircuits, typeCircuitStamp]);
  const assignGroups = useMemo(
    () => (assignMode?.isOn ? groupUnassigned(assignMode.devices) : []),
    [assignMode?.isOn, assignMode?.devices],
  );
  const derivedForAssign = edit?.derivedGeometry;
  const feedsForAssign = edit?.feedsByBoard;
  const assignTargets = useMemo(() => {
    if (!assignMode?.isOn || !derivedForAssign) return { circuits: [], boards: [] };
    const devicePositionsByCircuit: Record<string, { x: number; y: number }[]> = {};
    const colours: Record<string, string> = {};
    Object.entries(derivedForAssign.byCircuit).forEach(([circuitId, state]) => {
      devicePositionsByCircuit[circuitId] = state.paths.flatMap((path) => {
        const at = path.waypoints[path.waypoints.length - 1];
        return at ? [at] : [];
      });
      const rgb = state.paths[0]?.rgb;
      if (rgb) colours[circuitId] = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
    });
    return buildAssignTargets({
      selection: assignMode.selection,
      rows: items,
      feedingBoardByCircuit: edit?.feedingBoardByCircuit ?? {},
      categoriesByBoard: Object.fromEntries(
        Object.entries(feedsForAssign ?? {}).map(([label, resolved]) => [label, resolved.feed.categories]),
      ),
      devicePositionsByCircuit,
      boardPositions: Object.fromEntries(
        derivedForAssign.sources.map((source) => [source.label, { x: source.x, y: source.y }]),
      ),
      colorOf: (circuitId) => colours[circuitId],
    });
  }, [assignMode?.isOn, assignMode?.selection, derivedForAssign, feedsForAssign, items, edit?.feedingBoardByCircuit]);
  const splitRooms = (roomLayout?.rooms ?? []).map((room) => ({ id: room.id, name: room.name, points: room.points }));
  const splitDoors = (roomLayout?.doors ?? []).flatMap(
    (door): Array<[{ x: number; y: number }, { x: number; y: number }]> =>
      door.points.length >= 2 ? [[door.points[0], door.points[1]]] : [],
  );
  const splitPointsPerMetre = pageScaleValue ? 1 / pageScaleValue : 0;
  const isControlNamed = (name: string, deviceType?: DeviceType) => {
    const role = switchingRoleOf({ deviceType, name });
    return role === "control" || role === "twoWay";
  };
  const boardPositionOf = (boardLabel: string | null | undefined) => {
    const board = edit?.derivedGeometry?.sources.find((source) => source.label === boardLabel);
    return board ? { x: board.x, y: board.y } : null;
  };

  // The first group stays on the circuit; every other group becomes a circuit of its own on the same board, in one step.
  const planAutoSplit = (circuitId: string, maxPerCircuit: number, takeSwitches: boolean): AutoSplitPlan | null => {
    const geometry = edit?.derivedGeometry;
    if (!edit || !geometry) return null;
    const devicesOn = (id: string) =>
      (geometry.byCircuit[id]?.paths ?? []).flatMap((path) => {
        const at = path.waypoints[path.waypoints.length - 1];
        if (!at) return [];
        const deviceType = path.elementId ? geometry.deviceTypeByElementId?.[path.elementId] : undefined;
        return [
          { id: path.destinationId, circuitId: id, position: at, isControl: isControlNamed(path.destinationName ?? "", deviceType) },
        ];
      });
    const boardLabel = edit.feedingBoardByCircuit[circuitId];
    const category = edit.categoryByCircuit[circuitId];
    // The agent may have put a room's switch on a circuit of its own; a room split must bring it to its lights.
    const available =
      category === "lighting"
        ? findRoomSwitches({
            circuitId,
            boardDevices: Object.keys(geometry.byCircuit)
              // Any board: the agent sometimes files a switch circuit under another one; nearest-light decides.
              .filter((id) => edit.categoryByCircuit[id] === category)
              .flatMap(devicesOn),
            pointsPerMetre: splitPointsPerMetre,
          })
        : [];
    const borrowed = takeSwitches ? available : [];
    const devices = [...devicesOn(circuitId), ...borrowed];
    const byRooms = splitByRooms({
      devices,
      rooms: splitRooms,
      doors: splitDoors,
      maxPerGroup: maxPerCircuit,
      boardPosition: boardPositionOf(boardLabel),
      pointsPerMetre: splitPointsPerMetre,
    });
    const switchCounts = { availableSwitchCount: available.length, borrowedSwitchCount: borrowed.length };
    if (byRooms) return { method: "rooms", groups: byRooms, ...switchCounts };
    return {
      method: "rows",
      groups: splitIntoRows({ devices, maxPerGroup: maxPerCircuit }).map((deviceIds) => ({ deviceIds, roomIds: [], roomNames: [] })),
      ...switchCounts,
    };
  };
  const autoSplitCircuit = (circuitId: string, plan: AutoSplitPlan) => {
    const geometry = edit?.derivedGeometry;
    if (!edit || !geometry) return;
    if (plan.groups.length < 2 && plan.borrowedSwitchCount === 0) return;
    const boardLabel = edit.feedingBoardByCircuit[circuitId];
    if (!boardLabel) return;
    const boardName = geometry.sources.find((source) => source.label === boardLabel)?.name ?? boardLabel;
    const row = items.find((item) => item.circuit === circuitId);
    const category = edit.categoryByCircuit[circuitId] ?? null;
    const takenIds = new Set([...Object.keys(geometry.byCircuit), ...Object.keys(edit.addedCircuits)]);
    const [staying, ...leaving] = plan.groups;
    const batches = [
      // Renamed in the same step: the circuit that stays now covers fewer rooms.
      {
        destinationIds: staying.deviceIds,
        adoptions: [],
        name: roomCircuitName({ roomNames: staying.roomNames }),
        target: { kind: "circuit" as const, circuitId },
      },
      ...leaving.map((group) => {
        const key = nextCircuitKey({ category, boardName, takenIds });
        takenIds.add(mintCircuitId({ key, boardName }));
        return {
          destinationIds: group.deviceIds,
          adoptions: [],
          name: roomCircuitName({ roomNames: group.roomNames }),
          target: {
            kind: "newCircuit" as const,
            circuit: {
              key,
              sourceLabel: boardLabel,
              boardName,
              cableType: edit.cableTypeByCircuit[circuitId] ?? row?.cable_type ?? "",
              ...(row?.system ? { system: row.system } : {}),
            },
          },
        };
      }),
    ];
    edit.assignDevicesBatch(batches);
  };

  // Every board that feeds the shown category, for "Aus Räumen bilden".
  const roomCircuitBoards = (() => {
    const category = assignMode?.category;
    if (!category || !edit?.derivedGeometry) return [];
    return edit.derivedGeometry.sources
      .filter((source) => feedsForAssign?.[source.label]?.feed.categories.includes(category))
      .map((source) => ({ label: source.label, name: source.name, position: { x: source.x, y: source.y } }));
  })();
  const deviceBoardOf = (device: AssignableDevice): string | null => {
    if (device.kind === "onCircuit") return edit?.feedingBoardByCircuit[device.circuitId] ?? null;
    // A device on no circuit goes to the nearest board that feeds its kind.
    const nearest = roomCircuitBoards.reduce<(typeof roomCircuitBoards)[number] | null>(
      (best, board) =>
        !best ||
        Math.hypot(board.position.x - device.position.x, board.position.y - device.position.y) <
          Math.hypot(best.position.x - device.position.x, best.position.y - device.position.y)
          ? board
          : best,
      null,
    );
    return nearest?.label ?? null;
  };
  const planRoomCircuitsFor = (
    boardLabel: string,
    maxPerCircuit: number,
    includePlanCircuits: boolean,
  ): RoomCircuitsPlan | null => {
    if (!assignMode?.isOn || !edit) return null;
    const devices = assignMode.devices.filter((device) => deviceBoardOf(device) === boardLabel);
    const keptCircuitIds = new Set(
      includePlanCircuits
        ? []
        : devices.flatMap((device) =>
            device.kind === "onCircuit" && circuitNames?.[device.circuitId]?.from === "plan" ? [device.circuitId] : [],
          ),
    );
    return planRoomCircuits({
      devices: devices.map((device) => ({
        key: device.key,
        position: device.position,
        isControl: isControlNamed(device.name),
        circuitId: device.kind === "onCircuit" ? device.circuitId : null,
      })),
      keptCircuitIds,
      rooms: splitRooms,
      doors: splitDoors,
      maxPerGroup: maxPerCircuit,
      boardPosition: boardPositionOf(boardLabel),
      pointsPerMetre: splitPointsPerMetre,
    });
  };
  const applyRoomCircuits = (boardLabel: string, plan: RoomCircuitsPlan) => {
    const geometry = edit?.derivedGeometry;
    const category = assignMode?.category ?? null;
    if (!edit || !geometry || !assignMode || !category) return;
    const boardName = geometry.sources.find((source) => source.label === boardLabel)?.name ?? boardLabel;
    const deviceByKey = new Map(assignMode.devices.map((device) => [device.key, device]));
    const takenIds = new Set([...Object.keys(geometry.byCircuit), ...Object.keys(edit.addedCircuits)]);
    const batches = plan.groups.map((group) => {
      const members = group.deviceKeys.flatMap((key) => deviceByKey.get(key) ?? []);
      const destinationIds = members.flatMap((device) => (device.kind === "noCircuit" ? [] : [device.destinationId]));
      const adoptions = members.flatMap((device) =>
        device.kind === "noCircuit" ? [{ elementId: device.elementId, position: device.position, name: device.name }] : [],
      );
      const name = roomCircuitName({ roomNames: group.roomNames });
      if (group.circuitId) {
        return { destinationIds, adoptions, name, target: { kind: "circuit" as const, circuitId: group.circuitId } };
      }
      const key = nextCircuitKey({ category, boardName, takenIds });
      takenIds.add(mintCircuitId({ key, boardName }));
      return {
        destinationIds,
        adoptions,
        name,
        target: {
          kind: "newCircuit" as const,
          circuit: {
            key,
            sourceLabel: boardLabel,
            boardName,
            cableType: CATEGORY_META[category].defaultCableType,
            system: CATEGORY_META[category].system,
          },
        },
      };
    });
    edit.assignDevicesBatch(batches);
  };
  const isUnroutedListOpen = openSection === "unrouted";
  useEffect(() => {
    onUnroutedListOpenChange?.(isUnroutedListOpen);
    return () => onUnroutedListOpenChange?.(false);
  }, [onUnroutedListOpenChange, isUnroutedListOpen]);
  const boardNameOf = (label: string | null): string =>
    label === null
      ? t("smartSurveyor.cablesBeta.runsNotFed")
      : (feedPointOptions?.find((option) => option.label === label)?.name ??
        label);

  const visibleItems = useMemo(
    () =>
      filterCableRows({
        items: typeItems,
        query,
        circuitKeys: allCircuitKeys,
        circuitBreakers: allCircuitBreakers,
      }),
    [typeItems, query, allCircuitKeys, allCircuitBreakers],
  );
  const isSearching = !!query.trim();
  // The eye answers ONE question — "is the plan drawing every cable?" — and
  // isolating a run is an answer of no, whatever the switch underneath says.
  // Reading the raw switch instead is what made it look broken: turning it on
  // with a run isolated changed nothing on the drawing, because isolation
  // outranks it there.
  //
  // `selectedCircuit` is what the plan is ACTUALLY narrowed to, Karl's focus
  // included — and `onSelectCircuit(null)` now lifts his too, so this control
  // can never report a state it cannot reach.
  const areAllCablesDrawn = areAllCablesShown && !selectedCircuit;
  const toggleAllCables = () => {
    if (areAllCablesDrawn) {
      onToggleAllCables?.(false);
      return;
    }
    // Both halves, in the order he asked for them: *"it closes that one, and
    // shows all runs"*. Clearing alone leaves him the devices-only plan he
    // pressed the eye to get out of.
    onSelectCircuit?.(null);
    onToggleAllCables?.(true);
  };
  // The boards actually on this plan, carrying the installation each one is —
  // a run created on the safety board is a safety run, and lands in its own
  // section rather than among the sockets.
  const boardOptions = useMemo<AddRunBoardOption[]>(() => {
    const systemByLabel = new Map(
      (edit?.baseSources ?? []).map((source) => [source.label, source.system]),
    );
    return (feedPointOptions ?? []).map((option) => ({
      ...option,
      system: systemByLabel.get(option.label),
    }));
  }, [feedPointOptions, edit?.baseSources]);
  // What the rows PRINT, which is the only thing a duplicate would be visible
  // against: two runs leading with one id are indistinguishable in every picker
  // on this panel.
  const addedCircuitIds = useMemo(
    () => Object.keys(addedCircuits),
    [addedCircuits],
  );
  const takenRunKeys = useMemo(
    () =>
      Object.values(allCircuitKeys ?? {}).filter((key): key is string => !!key),
    [allCircuitKeys],
  );

  if (!takeoff) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-slate-400">
        <Cable size={28} className="text-slate-300" />
        <div>{t("smartSurveyor.cablesBeta.noEstimate")}</div>
        <div className="text-xs">
          {t("smartSurveyor.cablesBeta.noEstimateHint")}
        </div>
      </div>
    );
  }

  // Runs a removed group can be moved onto instead of simply put back, each
  // carrying the colour it is drawn in — he picks a run by its colour.
  const removedGroupCircuitOptions = items
    .filter((item) => !!item.circuit)
    .map((item) => {
      const circuitId = item.circuit as string;
      const rgb = edit?.derivedGeometry?.byCircuit[circuitId]?.paths[0]?.rgb;
      return {
        circuitId,
        label: item.text || circuitId,
        color: rgb ? `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` : undefined,
      };
    });
  const unroutedCircuitOptions = removedGroupCircuitOptions.map((option) => ({
    id: option.circuitId,
    label: option.label,
    color: option.color,
  }));
  // `pageScaleValue` is metres per PDF point; the tray quantities need its
  // inverse. Zero when the plan is uncalibrated, and the roster prints 0 m
  // rather than dividing by it.
  const pointsPerMetre = pageScaleValue ? 1 / pageScaleValue : 0;
  const round1 = (n: number) => Math.round(n * 10) / 10;
  const runTotal = round1(takeoff.total_m || 0);
  // Always the app's own number: the agent's total is a comparison line, never the headline.
  const useDerived = Boolean(edit?.derivedGeometry);
  const total = useDerived ? round1(edit?.derivedTotalM ?? 0) : runTotal;
  const provisionalReasons =
    edit && (edit.routing.phase !== "idle" || edit.routeRequests.length > 0)
      ? [t("smartSurveyor.cablesBeta.provisionalRoutesPending")]
      : [];

  const runName = (circuitId: string | null): string | null => {
    if (!circuitId) return null;
    const key = allCircuitKeys?.[circuitId];
    if (key) return key;
    return items.find((item) => item.circuit === circuitId)?.text || null;
  };

  // What the step WAS, in his words. Only a reshape reaches the geometry, and
  // only for the one run it names.
  const stepPhrase = (step: EditStepSummary | null): string | null => {
    if (!step) return null;
    const run = runName(
      stepRunId({ summary: step, geometry: edit?.derivedGeometry ?? null }),
    );
    return t(editStepPhraseKey({ kind: step.kind, hasRun: !!run }), {
      count: step.count,
      run,
    });
  };

  // A disabled button says why it is disabled and never what it used to do:
  // both phrases are null the moment the stack behind them is empty.
  const undoPhrase = stepPhrase(edit?.undoStep ?? null);
  const redoPhrase = stepPhrase(edit?.redoStep ?? null);
  const undoTitle = undoPhrase
    ? t("smartSurveyor.cablesBeta.undoStepHint", { what: undoPhrase })
    : t("smartSurveyor.cablesBeta.undoNoHistory");
  const redoTitle = redoPhrase
    ? t("smartSurveyor.cablesBeta.redoStepHint", { what: redoPhrase })
    : t("smartSurveyor.cablesBeta.redoNothing");

  const scaleText = pageScaleValue
    ? t("smartSurveyor.cablesBeta.scaleValueWithSource", {
        value: parseFloat(pageScaleValue.toFixed(4)),
        source: t(
          pageScaleSource === "manual"
            ? "smartSurveyor.pageCalibration.sourceUserSet"
            : "smartSurveyor.pageCalibration.sourceAiDetected",
        ),
      })
    : t("smartSurveyor.cablesBeta.scaleNotSet");

  // Opening one closes whichever was open. Four sections that each kept their
  // own answer could all be open at once, and then nothing else on the panel
  // was reachable without scrolling past them.
  const disclosureOf = (section: AsideSection) => ({
    isOpen: openSection === section,
    onOpenChange: (next: boolean) => setOpenSection(next ? section : null),
  });

  const isSetupOpen = isSetupOpenByHim;
  // Every count on the row is the SAME number its section prints, which is why
  // the feed points are counted by the roster the section itself builds rather
  // than by adding two records up here.
  const sourceRoster = edit?.derivedGeometry
    ? buildSourceRoster({
        baseSources: edit.baseSources,
        addedSources: edit.addedSources,
        removedSources: edit.removedSources,
        sourceNames: edit.sourceNames,
        feedingBoardByCircuit: edit.feedingBoardByCircuit,
        lengthsByCircuit: edit.derivedLengthsByCircuit,
      })
    : [];
  const setupTrayTypes = [...new Set((trays ?? []).map((tray) => tray.type))].sort();
  const setupProgress = edit && isSettingUp
    ? buildSetupProgress({
        boards: sourceRoster,
        mainBoard: edit.mainBoard,
        feederCircuits: edit.feederCircuits,
        feedPointByCircuit: edit.feedPointByCircuit,
        feederOffFloor: edit.feederOffFloor,
        cableTypeByCircuit: edit.cableTypeByCircuit,
        storedBoardFeeds: edit.storedBoardFeeds,
        areaFeeds: edit.areaFeeds,
        storedHeights: edit.storedHeights,
        trayTypes: setupTrayTypes,
        boardsConfirmed: edit.boardsConfirmed,
        heightsConfirmed: edit.heightsConfirmed,
        traysConfirmed: edit.trayHeightsConfirmed || setupTrayTypes.length === 0,
        walls: detectedWallsSummary
          ? {
              counts: countWallReview({ walls: walls?.walls ?? [], summary: detectedWallsSummary }),
              confirmed: edit.wallsConfirmed,
            }
          : undefined,
      })
    : null;
  const liveSourceCount = sourceRoster.filter(
    (entry) => !entry.isRemoved,
  ).length;
  const removedCount = edit?.removedDeviceMarkers.length ?? 0;
  // TYPES, which is what the tray section's own chip counts. `trays.length` is
  // segments, and there are hundreds of those.
  const trayTypeCount = trays?.length
    ? buildTrayRoster({ trays, pointsPerMetre }).length
    : 0;
  const unroutedCount = unrouted?.total ?? 0;

  const setupCounts: PlanSetupCount[] = [];
  if (liveSourceCount > 0)
    setupCounts.push({
      tone: "neutral",
      count: liveSourceCount,
      label: t("smartSurveyor.cablesBeta.planSetupFeedPoints"),
    });
  if (removedCount > 0)
    setupCounts.push({
      tone: "removed",
      count: removedCount,
      label: t("smartSurveyor.cablesBeta.planSetupRemoved"),
    });
  if (trayTypeCount > 0)
    setupCounts.push({
      tone: "tray",
      count: trayTypeCount,
      label: t("smartSurveyor.cablesBeta.planSetupTrays"),
    });
  if (rooms && rooms.rooms.length > 0)
    setupCounts.push({
      tone: "room",
      count: rooms.rooms.length,
      label: t("smartSurveyor.cablesBeta.roomsTitle"),
    });
  const wallOnlyCount = walls?.walls.filter((wall) => wall.kind === "wall").length ?? 0;
  if (wallOnlyCount > 0)
    setupCounts.push({
      tone: "wall",
      count: wallOnlyCount,
      label: t("smartSurveyor.cablesBeta.wallsTitle"),
    });
  if (unroutedCount > 0)
    setupCounts.push({
      tone: "unrouted",
      count: unroutedCount,
      label: t("smartSurveyor.cablesBeta.planSetupUnrouted"),
    });

  const areaCountByLabel: Record<string, number> = {};
  (edit?.areaFeeds ?? []).forEach((area) => {
    areaCountByLabel[area.sourceLabel] =
      (areaCountByLabel[area.sourceLabel] ?? 0) + 1;
  });

  // Whether anything is IN there, not whether anything is counted: with every
  // board struck the roster still has rows and he needs the way back to them,
  // and a group hidden on a zero count is a struck board he cannot restore.
  const hasSetupContent =
    sourceRoster.length > 0 ||
    removedCount > 0 ||
    trayTypeCount > 0 ||
    unroutedCount > 0 ||
    // The wall tool has to be reachable on a plan with no walls on it yet, or
    // there is no way to draw the first one.
    Boolean(walls);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Every fact on its OWN line, and every line nowrap.
          The row this replaces was a flex container holding a bare
          interpolated string: an anonymous flex item shrinks to min-content
          and wraps word by word, which is what stacked "79 / circuits /
          · 567 / devices" into ragged columns. It was never a width problem,
          so no width fixes it — only taking the text out of the flex row.
          A fact may SHARE a row with a control, as long as it is still its own
          element: what may never happen is a bare string in a flex row. */}
      <div data-testid="cables-header" className="flex-none px-1 pb-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {!isResultHidden && (
            <div className="min-w-0 truncate text-[26px] font-bold leading-none tabular-nums text-emerald-600">
              {metres(total)} m
            </div>
          )}
          {useDerived && !isResultHidden && provisionalReasons.length > 0 && (
            <span
              title={provisionalReasons.join(" · ")}
              className="shrink-0 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[12px] font-semibold text-amber-700"
            >
              {t("smartSurveyor.cablesBeta.provisional")}
            </span>
          )}
          {/* All three together, beside the loudest thing on the panel. They had
              a row of their own, and it cost one on a column that has none to
              spare — this corner is the first place he looks, not the last.
              Reset stays the weakest of the three and sits last. The row wraps
              rather than clips, as insurance: German spends 371 of the 399 this
              column now has, and spent more than it had at the old 380. */}
          {edit && (edit.hasEdits || edit.canUndo || edit.canRedo) && (
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {/* The step is named on the control itself, never on a line of
                  its own: this row has no room for a sentence beside the
                  buttons — see the width numbers in KARL.md — and a sentence
                  there is one that pushes a control off the panel. */}
              <button
                type="button"
                onClick={edit.undo}
                disabled={!edit.canUndo}
                aria-label={t("smartSurveyor.cablesBeta.undo")}
                title={undoTitle}
                className={HISTORY_CLASS}
              >
                <Undo2 size={18} aria-hidden />
              </button>
              <button
                type="button"
                onClick={edit.redo}
                disabled={!edit.canRedo}
                aria-label={t("smartSurveyor.cablesBeta.redo")}
                title={redoTitle}
                className={HISTORY_CLASS}
              >
                <Redo2 size={18} aria-hidden />
              </button>
              {edit.hasEdits && (
                <button
                  type="button"
                  onClick={() => {
                    trackEvent(
                      ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_TAKEOFF_RESET,
                    );
                    (onResetAll ?? edit.resetAll)();
                  }}
                  className="flex h-9 shrink-0 items-center px-1 text-[13px] font-medium text-slate-500 hover:text-red-600 hover:underline"
                >
                  {t("smartSurveyor.cablesBeta.resetAllEdits")}
                </button>
              )}
            </div>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2">
          {/* Devices only. The circuit count moved onto the runs block's own
              header, which is the list it counts — each number printed once,
              on the thing it describes. */}
          <div className="min-w-0 flex-1 truncate whitespace-nowrap text-[13px] text-slate-500">
            {t("smartSurveyor.cablesBeta.summaryDevices", {
              devices: takeoff.device_count ?? 0,
            })}
          </div>
          {/* The scale shares the counts line instead of taking one of its own:
              it is a value he sets once, and the value IS the control. The
              words "Change scale" are the tooltip and the accessible name, so
              the pencil is glossed rather than left to be guessed. */}
          {(pageScaleValue !== undefined || onChangePageScale) &&
            (onChangePageScale ? (
              <button
                type="button"
                onClick={onChangePageScale}
                disabled={isChangePageScaleDisabled}
                aria-label={`${t("smartSurveyor.cablesBeta.changeScale")} · ${scaleText}`}
                title={t("smartSurveyor.cablesBeta.changeScale")}
                className="flex max-w-[60%] flex-none items-center gap-1 rounded px-1 text-[13px] text-slate-500 hover:text-blue-700 hover:underline disabled:opacity-50 disabled:hover:text-slate-500 disabled:hover:no-underline"
              >
                <span className="truncate whitespace-nowrap">{scaleText}</span>
                <Pencil size={13} aria-hidden className="flex-none" />
              </button>
            ) : (
              <div className="max-w-[60%] flex-none truncate whitespace-nowrap px-1 text-[13px] text-slate-500">
                {scaleText}
              </div>
            ))}
        </div>
        {/* At the top, beside the number it moves: under Plangrundlagen and on each run it was not found. */}
        {edit?.rerouteAllCircuits && !isResultHidden && (
          <div className="mt-2">
            <PlanRecalculate
              isStale={edit.arePlanTraysStale}
              isRunning={edit.isReroutingPlan}
              summary={edit.planRerouteSummary}
              onRecalculate={() => void recalculate()}
              changedCount={edit.circuitsNeedingRoute.length}
              onRecalculateChanged={() => void recalculate(edit.circuitsNeedingRoute)}
              onDismissSummary={edit.dismissPlanRerouteSummary}
              formatMetres={metres}
            />
          </div>
        )}
      </div>

      {takeoff.error && (
        <div className="mx-1 mb-2 flex-none rounded bg-amber-50 px-2 py-1 text-[13px] text-amber-700">
          ⚠ {takeoff.error}
        </div>
      )}

      {!isResultHidden && (
        <div className="mb-2 flex-none">
          <CableTypeSummary
            entries={cableTypeSummary}
            selectedKey={cableTypeKey}
            onSelect={setCableTypeKey}
            formatMetres={metres}
            boardName={boardNameOf}
            onCreate={edit ? (name) => edit.addCableType(name) : undefined}
            createdTypes={edit?.addedCableTypes ?? []}
            onRemove={edit ? (name) => edit.removeCableType(name) : undefined}
          />
        </div>
      )}
      {/* A line of its own, and only while a grab is live: it is a sentence
          with a way out in it, and it needs the width. */}
      {grabbed && (
        <div className="mb-2 flex-none px-2 text-[13px] text-amber-700">
          {grabbed.kind === "main_board"
            ? t(
                grabbed.isFeedIn
                  ? "smartSurveyor.cablesBeta.pickingFeedInHint"
                  : "smartSurveyor.cablesBeta.pickingMainBoardHint",
              )
            : grabbed.kind === "upstream_for"
            ? t("smartSurveyor.cablesBeta.pickingUpstreamHint", {
                board: edit?.sourceNames[grabbed.label] ?? grabbed.label,
              })
            : grabbed.kind === "feeder_for"
            ? t("smartSurveyor.cablesBeta.pickingFeederHint", {
                category: t(`smartSurveyor.cablesBeta.categories.${grabbed.label}`),
              })
            : grabbed.kind === "new_source"
            ? t("smartSurveyor.cablesBeta.placingSourceHint", {
                source: grabbed.label,
              })
            : t("smartSurveyor.cablesBeta.movingHint", {
                target:
                  grabbed.kind === "source"
                    ? t("smartSurveyor.cablesBeta.movingSourceTarget")
                    : t("smartSurveyor.cablesBeta.movingNodeTarget"),
              })}{" "}
          <button
            onClick={onCancelGrab}
            className="text-blue-600 hover:underline"
          >
            {t("smartSurveyor.cablesBeta.cancel")}
          </button>
        </div>
      )}
      {zoneDraft && (
        <div className="mb-2 flex-none px-2 text-[13px] text-blue-700">
          {t("smartSurveyor.cablesBeta.askArmedZone", {
            board:
              edit?.sourceNames?.[zoneDraft.sourceLabel] ??
              zoneDraft.sourceLabel,
          })}{" "}
          {zoneDraft.points.length >= 3 && (
            <button
              onClick={onFinishZone}
              className="mr-2 font-semibold text-blue-600 hover:underline"
            >
              {t("smartSurveyor.cablesBeta.askZoneClose")}
            </button>
          )}
          <button
            onClick={onCancelGrab}
            className="text-blue-600 hover:underline"
          >
            {t("smartSurveyor.cablesBeta.cancel")}
          </button>
        </div>
      )}
      {/* THE RUNS LEAD. They are what he opened the panel for, and they used to
          start two thirds of the way down it, under four collapsed rows and a
          search field stranded in the middle of the column. Their own block, in
          the same shell as everything else, so the list reads as one thing:
          *"or even seperate out those two sections"*. */}
      {assignMode?.isOn && assignMode.showsPanel && edit ? (
        <AssignModePanel
          selection={assignMode.selection}
          groups={assignGroups}
          targets={assignTargets}
          boardFilter={assignMode.board}
          remainingCount={assignMode.devices.length}
          splittingName={assignMode.circuitId ? (runName(assignMode.circuitId) ?? assignMode.circuitId) : null}
          boardName={(label) => boardNameOf(label)}
          categoryName={(category) => t(CATEGORY_META[category].i18nKey)}
          canRemove={assignMode.devices.some(
            (device) => device.kind === "onCircuit" && assignMode.selectedKeys.includes(device.key),
          )}
          onAssignToCircuit={(circuitId) => {
            assignMode.assignTo({ kind: "circuit", circuitId });
          }}
          missingFeeders={assignMode.missingFeeders}
          onPickFeeder={assignMode.pickFeederFor}
          onLeaveOut={assignMode.leaveCategoryOut}
          onNewCircuit={(boardLabel, typedKey) => {
            const source = edit.derivedGeometry?.sources.find((one) => one.label === boardLabel);
            const boardName = source?.name ?? boardLabel;
            const category = assignMode.selection.categories[0] ?? null;
            const key =
              typedKey?.trim() ||
              nextCircuitKey({
                category,
                boardName,
                takenIds: new Set([
                  ...Object.keys(edit.derivedGeometry?.byCircuit ?? {}),
                  ...Object.keys(edit.addedCircuits),
                ]),
              });
            assignMode.assignTo({
              kind: "newCircuit",
              circuit: {
                key,
                sourceLabel: boardLabel,
                boardName,
                cableType: category ? CATEGORY_META[category].defaultCableType : "",
                ...(category ? { system: CATEGORY_META[category].system } : {}),
              },
            });
          }}
          onRemove={assignMode.removeSelected}
          onClearSelection={() => assignMode.setSelected([])}
          onSelectKeys={assignMode.setSelected}
          onDone={assignMode.exit}
          categories={assignMode.categories}
          activeCategory={assignMode.category}
          roomCircuits={
            assignMode.circuitId || !assignMode.category || splitRooms.length === 0
              ? undefined
              : {
                  boards: roomCircuitBoards.map((board) => ({ label: board.label, name: board.name })),
                  categoryName: t(CATEGORY_META[assignMode.category].i18nKey),
                  defaultSize: DEFAULT_SPLIT_SIZE[assignMode.category] || FALLBACK_SPLIT_SIZE,
                  plan: planRoomCircuitsFor,
                  circuitName: (circuitId) => runName(circuitId) ?? circuitId,
                  onPreview: (plan) =>
                    onRoomTint?.(
                      plan
                        ? Object.fromEntries(
                            plan.groups.flatMap((group, index) => group.roomIds.map((roomId) => [roomId, index])),
                          )
                        : null,
                    ),
                  onTake: applyRoomCircuits,
                }
          }
          onPickCategory={assignMode.setCategory}
        />
      ) : isSettingUp && setupProgress ? (
        <PlanSetupProgress
          progress={setupProgress}
          formatHeight={(heightM) => `${formatMetres({ value: heightM, language: i18n.language, fractionDigits: 2 })} m`}
          onHighlightTrayType={onHighlightTrayType}
        />
      ) : isFirstCalculationPending ? (
        <section
          data-testid="cable-runs-calculating"
          className="flex flex-none flex-col gap-1 rounded-lg border border-dashed border-blue-300 bg-blue-50/50 px-3 py-4 text-[13.5px] leading-snug text-slate-600"
        >
          <span className="flex items-center gap-2 font-semibold text-slate-800">
            <Loader2 size={15} className="animate-spin text-blue-600" aria-hidden />
            {t("smartSurveyor.cablesBeta.setupProgressCalculating")}
          </span>
          {t("smartSurveyor.cablesBeta.setupProgressCalculatingDetail")}
        </section>
      ) : (
        <section
          data-testid="cable-runs"
          className="flex min-h-[8rem] flex-1 flex-col overflow-hidden rounded-lg border border-slate-300 bg-white shadow-sm"
        >
          <PanelSectionHeader
            tone="neutral"
            title={t("smartSurveyor.cablesBeta.runsTitle")}
            // His own runs are counted with the estimation's: the chip has to
            // answer "how many runs am I looking at", and a list longer than its
            // own header is a total he cannot sign.
            count={(takeoff.circuit_count ?? publishedItems.length) + addedRows.length + appRows.length}
            action={
              // TWO controls here, against the slot's own "one action at most"
              // rule, and deliberately: both are about this list of runs, both
              // have to be reachable without opening anything, and the title is
              // one short word with a chip beside it. The eye leads because it
              // decides what is on the drawing he is looking at.
              <span className="flex flex-none items-center">
                {assignMode && (
                  <button
                    type="button"
                    onClick={() => assignMode.enter()}
                    title={t("smartSurveyor.cablesBeta.assignModeEnterHint")}
                    className="mr-1 flex h-9 flex-none items-center gap-1 rounded-md border border-blue-200 bg-white px-2 text-[13px] font-medium text-blue-700 hover:border-blue-400 hover:bg-blue-50"
                  >
                    <MousePointerSquareDashed size={15} aria-hidden />
                    {t("smartSurveyor.cablesBeta.assignModeEnter")}
                  </button>
                )}
                {onToggleAllCables && (
                  <button
                    type="button"
                    aria-pressed={areAllCablesDrawn}
                    onClick={toggleAllCables}
                    title={t(
                      areAllCablesDrawn
                        ? "smartSurveyor.cablesBeta.cablesHide"
                        : "smartSurveyor.cablesBeta.cablesShow",
                    )}
                    aria-label={t(
                      areAllCablesDrawn
                        ? "smartSurveyor.cablesBeta.cablesHide"
                        : "smartSurveyor.cablesBeta.cablesShow",
                    )}
                    className={clsx(
                      "grid h-9 w-9 flex-none place-items-center rounded-md border",
                      areAllCablesDrawn
                        ? "border-slate-300 bg-white text-slate-700"
                        : "border-transparent text-slate-400 hover:border-slate-300 hover:bg-white",
                    )}
                  >
                    {areAllCablesDrawn ? (
                      <Eye size={17} />
                    ) : (
                      <EyeOff size={17} />
                    )}
                  </button>
                )}
                {edit && (
                  // On the header, never inside the list: the same rule the feed
                  // points follow — he cannot find a way to CREATE a run behind
                  // eighty rows he has to scroll past first.
                  <button
                    type="button"
                    onClick={() => setIsAddingRun((open) => !open)}
                    title={t("smartSurveyor.cablesBeta.runAdd")}
                    aria-label={t("smartSurveyor.cablesBeta.runAdd")}
                    className="mr-1.5 grid h-9 w-9 flex-none place-items-center rounded-md border border-transparent text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                  >
                    <Plus size={18} />
                  </button>
                )}
              </span>
            }
          />
          {/* Finding a run among 83 by scrolling is the thing this replaces — "i
              can search like A121". It sits ON the list it narrows, directly
              under its header, and narrows the LIST and nothing else: what is
              drawn on the plan stays a deliberate selection, per KARL.md. */}
          <div className="flex-none border-t border-slate-200 px-2 py-1.5">
            {/* A plan drawing 567 device dots and no cable between them reads as
                broken unless something says otherwise — the same reason the tray
                list prints a line when its layer is off. The sentence carries the
                way back, so he never has to find the eye to undo it. */}
            {onToggleAllCables && !areAllCablesShown && !selectedCircuit && (
              <div className="mb-1.5 flex items-center gap-2 px-1 text-[13px]">
                <span className="min-w-0 flex-1 truncate whitespace-nowrap text-slate-500">
                  {t("smartSurveyor.cablesBeta.cablesHidden")}
                </span>
                <button
                  type="button"
                  onClick={toggleAllCables}
                  className="flex-none rounded px-1 font-medium text-blue-700 hover:underline"
                >
                  {t("smartSurveyor.cablesBeta.cablesShowShort")}
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-2 focus-within:border-blue-400">
              <Search
                size={15}
                aria-hidden
                className="flex-none text-slate-400"
              />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label={t("smartSurveyor.cablesBeta.searchRuns")}
                placeholder={t("smartSurveyor.cablesBeta.searchRuns")}
                className="h-9 w-full min-w-0 bg-transparent text-[14px] text-slate-800 placeholder:text-slate-400 focus:outline-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label={t("smartSurveyor.cablesBeta.searchClear")}
                  title={t("smartSurveyor.cablesBeta.searchClear")}
                  className="flex-none text-slate-400 hover:text-slate-700"
                >
                  <X size={15} />
                </button>
              )}
            </div>
            {/* What he is looking at now, so a filtered total is never mistaken
                for the plan's. */}
            {isSearching && (
              <div className="mt-1 truncate whitespace-nowrap px-1 text-[13px] text-slate-500">
                {t("smartSurveyor.cablesBeta.searchMatches", {
                  shown: visibleItems.length,
                  total: items.length,
                })}
              </div>
            )}
            {/* A property of the LIST, not of the total — so it sits on the list
                it describes. The only other way back is clicking the isolated row
                again, which he has to already know. */}
            {onSelectCircuit && selectedCircuit && (
              <div className="mt-1 flex items-center gap-2 px-1 text-[13px]">
                <span className="min-w-0 flex-1 truncate whitespace-nowrap font-medium text-blue-800">
                  {t("smartSurveyor.cablesBeta.showingOneCircuit")}
                </span>
                <button
                  type="button"
                  onClick={() => onSelectCircuit(null)}
                  className="flex-none rounded px-1 font-medium text-blue-700 hover:underline"
                >
                  {t("smartSurveyor.cablesBeta.showAllRuns")}
                </button>
              </div>
            )}
          </div>
          {isAddingRun && edit && (
            <div className="flex-none px-2 pb-1">
              <AddRunForm
                ways={unfilledWays ?? []}
                boards={boardOptions}
                cableTypeOptions={allCableTypes}
                onCreateCableType={(name) => edit.addCableType(name)}
                takenKeys={takenRunKeys}
                onCancel={() => setIsAddingRun(false)}
                onCreate={(run: NewRun) => {
                  const circuitId = edit.addCircuit(run);
                  setIsAddingRun(false);
                  // Selected the moment it exists: an empty run at the bottom of
                  // eighty is a thing he made and cannot find, and the card he
                  // has to put devices on is the one that must be open.
                  if (circuitId) onSelectCircuit?.(circuitId);
                }}
              />
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-1">
            {isSearching && visibleItems.length === 0 ? (
              <div className="px-2 py-6 text-center text-[13px] text-slate-500">
                {t("smartSurveyor.cablesBeta.searchNoMatches", { query })}
              </div>
            ) : (
              <CircuitCardList
                items={visibleItems}
                formatMetres={metres}
                selectedCircuit={selectedCircuit}
                underReviewCircuit={underReviewCircuit}
                isPickingRun={isPickingRun}
                onPickRun={onPickRun}
                onAskKarlAbout={onAskKarlAbout}
                onSplit={assignMode ? (circuitId) => assignMode.enter(circuitId) : undefined}
                onAutoSplit={edit ? autoSplitCircuit : undefined}
                planAutoSplit={edit ? planAutoSplit : undefined}
                detailHost={detailHost}
                onSelectCircuit={onSelectCircuit}
                onHoverCircuit={onHoverCircuit}
                edit={edit}
                destinationInfo={destinationInfo}
                onHighlightDestination={onHighlightDestination}
                onShowHeightMarks={onShowHeightMarks}
                routeStatus={routeStatus}
                feedPointOptions={feedPointOptions}
                cableTypeOptions={allCableTypes}
                circuitKeys={allCircuitKeys}
                circuitBreakers={allCircuitBreakers}
                diagram={diagram}
                onRemoveCircuit={edit?.removeCircuit}
                addedCircuitIds={addedCircuitIds}
                openBoard={openBoard}
                onOpenBoard={onOpenBoard}
                isEveryBoardOpen={isSearching}
              />
            )}
          </div>
        </section>
      )}

      {/* Everything on this plan that is not a run, behind ONE row at the foot
          of the panel. Four rows of chrome above the list was the complaint;
          the row still says what is inside it, with the marks the plan draws,
          so he does not have to open it to see four devices are out. */}
      {/* Hidden while Karl walks the boards: these are the elements that step is still adjusting. */}
      {hasSetupContent && !isResultHidden && (
        <div
          data-testid="plan-setup"
          className="mt-1.5 flex-none overflow-hidden rounded-lg border border-slate-300 shadow-sm"
        >
          <PlanSetupRow
            title={t("smartSurveyor.cablesBeta.planSetupTitle")}
            counts={setupCounts}
            isOpen={isSetupOpen}
            onToggle={() => {
              setIsSetupOpenByHim(!isSetupOpen);
              // A closed group marks nothing on the plan, so the section
              // inside it does not stay open behind his back either.
              if (isSetupOpen) setOpenSection(null);
            }}
          />
          {/* One stack, one shell, one density — a wrong feed point is upstream
              of both the removals and the runs, so it still leads in here, one
              section open at a time, scrolling rather than clipping on a short
              window.

              INSIDE the group's card and on a tint, which is the whole of what
              says these four belong to the row above them. One ladder, four
              steps, each step a level of depth: the panel's canvas is
              `slate-50`, this group's header band `slate-200`, its body
              `slate-100`, and the sections in it stay white. The body must not
              share the canvas's tone — it did for one revision, and an open
              group read as four cards loose on the page again. */}
          {isSetupOpen && (
            <div
              data-testid="plan-setup-body"
              className="max-h-[min(32rem,46vh)] space-y-1.5 overflow-y-auto bg-slate-100 p-2"
            >
              {edit && edit.derivedGeometry && (
                <SourceList
                  {...disclosureOf("sources")}
                  areaCountByLabel={areaCountByLabel}
                  onFeedArea={onFeedArea}
                  onRemoveAreas={(label) =>
                    edit.areaFeeds
                      .filter((area) => area.sourceLabel === label)
                      .forEach((area) => edit.removeAreaFeed(area.id))
                  }
                  baseSources={edit.baseSources}
                  addedSources={edit.addedSources}
                  removedSources={edit.removedSources}
                  sourceNames={edit.sourceNames}
                  feedingBoardByCircuit={edit.feedingBoardByCircuit}
                  lengthsByCircuit={edit.derivedLengthsByCircuit}
                  formatMetres={metres}
                  onRename={edit.renameSource}
                  onRemoveTo={edit.removeSourceTo}
                  onRemove={edit.removeSource}
                  onRestore={edit.restoreSource}
                  feedsByBoard={edit.feedsByBoard}
                  typesByCategory={typesByCategory}
                  onSaveFeeds={edit.setBoardFeeds}
                  onAddSource={onAddSource}
                  isPlacingSource={grabbed?.kind === "new_source"}
                />
              )}
              {edit && (
                <RemovedDevices
                  {...disclosureOf("removed")}
                  removed={edit.removedDeviceMarkers}
                  groups={edit.removalGroups}
                  destinationInfoById={destinationInfoById}
                  onRestore={edit.restoreDevice}
                  onRestoreGroup={edit.restoreGroup}
                  circuitOptions={removedGroupCircuitOptions}
                  onAssignGroup={edit.assignGroupTo}
                  onAssignDevice={edit.assignDeviceTo}
                  onSpotlight={onSpotlightDevices}
                />
              )}
              {trays &&
                trays.length > 0 &&
                onToggleTrays &&
                onHighlightTrayType && (
                  <TrayList
                    {...disclosureOf("trays")}
                    trays={trays}
                    pointsPerMetre={pointsPerMetre}
                    formatMetres={metres}
                    areTraysShown={areTraysShown}
                    onToggleTrays={onToggleTrays}
                    onHighlightType={onHighlightTrayType}
                    drawnTypes={drawnRouteTypes}
                    onSetCarriesCable={onSetCarriesCable}
                  />
                )}
              {/* Beside the trays, because the two together are everything on
                  the plan that decides where cable may go: the tray is where it
                  wants to be, the wall is where it cannot. */}
              {walls && <WallList {...walls} {...disclosureOf("walls")} />}
              {rooms && rooms.rooms.length > 0 && <RoomList {...rooms} {...disclosureOf("rooms")} />}
              <UnroutedDevicesNote
                {...disclosureOf("unrouted")}
                summary={unrouted}
                onSpotlight={onSpotlightDevices}
                circuitOptions={unroutedCircuitOptions}
                onAdopt={edit?.adoptDevice}
                onOpenAssignMode={assignMode ? () => assignMode.enter() : undefined}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
